import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Circle as CircleIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  EventOutlined as EventIcon,
  History as HistoryIcon,
  HomeWorkOutlined as HomeWorkIcon,
  Payment as PaymentIcon,
  RefreshRounded,
  ReceiptLongRounded,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  type FormEvent,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
  memo,
} from "react";
import { getProperties, type PropertyRecord } from "../api/properties";
import { getRooms, type RoomRecord } from "../api/rooms";
import { getTenants, type TenantRecord } from "../api/tenants";
import {
  createTenancy,
  deleteTenancy,
  getTenancies,
  updateTenancy,
  type TenancyRecord,
} from "../api/tenancies";
import {
  createRentPayment,
  deleteRentPayment,
  getRentPayments,
  type RentPaymentRecord,
  updateRentPayment,
} from "../api/catalog";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useNotifications } from "../components/NotificationProvider";
import { formatDate } from "@/utils/formatDate";
import { normalizeDateInputValue } from "@/utils/date";

// --- Types ---
type TenancyFormState = {
  tenant_id: string;
  room_id: string;
  tenancy_start: string;
  payment_date: string;
  tenancy_end: string;
  monthly_rent: string;
  status: string;
  notes: string;
};

type PaymentFormState = {
  payment_date: string;
  payment_period: string;
  amount: string;
  payment_source: "tenant" | "government" | "other";
  payment_method: string;
  reference_no: string;
  notes: string;
};

type PaymentStatusFilterOption = "all" | "paid" | "unpaid";
type PaymentSourceFilterOption = "all" | "tenant" | "government" | "other";
type StatusFilterOption = "all" | "active" | "ended" | "notice_given";
type TenancyModalMode = "create" | "edit";

// --- Constants & Helpers ---
const PAYMENT_METHOD_OPTIONS = [
  "Bank transfer",
  "Cash",
  "Cheque",
  "Card",
  "Direct debit",
  "Other",
] as const;

const getCurrentMonthValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const getMonthKeyFromDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const createInitialFormState = (): TenancyFormState => ({
  tenant_id: "",
  room_id: "",
  tenancy_start: "",
  payment_date: new Date().toISOString().slice(0, 10),
  tenancy_end: "",
  monthly_rent: "",
  status: "active",
  notes: "",
});

const createInitialPaymentFormState = (): PaymentFormState => ({
  payment_date: new Date().toISOString().slice(0, 10),
  payment_period: getCurrentMonthValue(),
  amount: "",
  payment_source: "tenant",
  payment_method: "Bank transfer",
  reference_no: "",
  notes: "",
});

const normalizePeriodValue = (value: string) => {
  if (!value) return null;
  const [year, month] = value.split("-");
  if (!year || !month) return null;
  return `${year}-${month}-01`;
};

const getMonthKey = (dateString: string) => {
  if (!dateString) return "";
  const trimmed = dateString.trim();
  const monthMatch = trimmed.match(/^(\d{4}-\d{2})/);
  if (monthMatch) return monthMatch[1];

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return "";
  return getMonthKeyFromDate(parsed);
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 2,
  }).format(value);

const stringToColor = (string: string) => {
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

// --- Memoized Sub-Component for Performance ---
const TenancyCard = memo(
  ({
    tenancy,
    tenant,
    room,
    hasPaidCurrentMonth,
    recentStatuses,
    onEdit,
    onDelete,
    onRecordPayment,
    onViewHistory,
    onViewDetails,
  }: {
    tenancy: TenancyRecord;
    tenant?: TenantRecord;
    room?: RoomRecord;
    hasPaidCurrentMonth: boolean;
    recentStatuses: { key: string; label: string; paid: boolean }[];
    onEdit: (t: TenancyRecord) => void;
    onDelete: (t: TenancyRecord) => void;
    onRecordPayment: (t: TenancyRecord) => void;
    onViewHistory: (t: TenancyRecord) => void;
    onViewDetails: (t: TenancyRecord) => void;
  }) => {
    const tenantName = tenant
      ? `${tenant.first_name} ${tenant.last_name}`
      : "Unknown tenant";
    const roomNumber = room?.room_number ?? "Unknown";

    const getStatusColor = (status: string) => {
      switch (status) {
        case "active":
          return "success";
        case "ended":
          return "default";
        case "notice_given":
          return "warning";
        default:
          return "default";
      }
    };

    return (
      <Card
        variant="outlined"
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%", // Ensures full width
          borderRadius: 4,
          transition: "box-shadow 0.2s ease, border-color 0.2s ease",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
            borderColor: "primary.main",
          },
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 }, flexGrow: 1 }}>
          {/* Header Row: Avatar, Name, Status */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 3,
            }}
          >
            <Stack direction="row" spacing={2.5} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: stringToColor(tenantName),
                  width: 56,
                  height: 56,
                  fontWeight: 600,
                  fontSize: "1.3rem",
                }}
              >
                {tenantName.charAt(0)}
              </Avatar>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.25rem",
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  {tenantName}
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ color: "text.secondary" }}
                >
                  <HomeWorkIcon sx={{ fontSize: 18 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Room: {roomNumber}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
            <Chip
              label={tenancy.status.replace("_", " ")}
              size="medium"
              color={getStatusColor(tenancy.status)}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "capitalize",
                px: 1,
              }}
            />
          </Box>

          <Stack direction={{ xs: "column", md: "row" }} spacing={4}>
            <Box sx={{ flex: 1 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                sx={{ mb: 1.5 }}
              >
                <Box sx={{ flex: 1 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    sx={{ mb: 1.5 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ReceiptLongRounded fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Tenancy summary
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </Box>

            {/* Details Inner Container - Now spans 4 columns on desktop for full width usage */}
            <Box
              sx={{
                flex: 2,
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                gap: 3,
                p: 2.5,
                bgcolor: "background.default",
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "text.secondary",
                    fontWeight: 600,
                    mb: 0.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Monthly Rent
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {tenancy.monthly_rent
                    ? `${formatCurrency(Number(tenancy.monthly_rent))}`
                    : "Not set"}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "text.secondary",
                    fontWeight: 600,
                    mb: 0.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  <EventIcon sx={{ fontSize: 16 }} /> Pay Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {formatDate(tenancy.payment_date) ?? "Not set"}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "text.secondary",
                    fontWeight: 600,
                    mb: 0.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  <EventIcon sx={{ fontSize: 16 }} /> Start Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formatDate(tenancy.tenancy_start)}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "text.secondary",
                    fontWeight: 600,
                    mb: 0.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  <EventIcon sx={{ fontSize: 16 }} /> End Date
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: tenancy.tenancy_end
                      ? "text.primary"
                      : "text.secondary",
                  }}
                >
                  {formatDate(tenancy.tenancy_end) ?? "Open ended"}
                </Typography>
              </Box>
            </Box>

            {/* Payment Health Timeline - Placed alongside details on desktop */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  display: "block",
                  mb: 1.5,
                  lineHeight: 1,
                }}
              >
                Payment Health (Last 3 Months)
              </Typography>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ flexWrap: "wrap", gap: 1.5 }}
              >
                <Chip
                  icon={<CircleIcon sx={{ fontSize: "12px !important" }} />}
                  label={
                    hasPaidCurrentMonth ? "Current: Paid" : "Current: Unpaid"
                  }
                  color={hasPaidCurrentMonth ? "success" : "error"}
                  variant={hasPaidCurrentMonth ? "filled" : "outlined"}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    "& .MuiChip-icon": { ml: 1 },
                  }}
                />
                {recentStatuses.slice(1).map((entry) => (
                  <Chip
                    key={entry.key}
                    icon={
                      <CircleIcon
                        sx={{
                          fontSize: "10px !important",
                          color: entry.paid ? "success.main" : "text.disabled",
                        }}
                      />
                    }
                    label={`${entry.label}: ${entry.paid ? "Paid" : "Unpaid"}`}
                    color={entry.paid ? "success" : "default"}
                    variant={entry.paid ? "filled" : "outlined"}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 500,
                      color: entry.paid ? "common.white" : "text.secondary",
                      "& .MuiChip-icon": { ml: 1 },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </CardContent>

        <Divider />

        {/* Footer Actions */}
        <CardActions
          sx={{
            p: 2,
            px: { xs: 2, sm: 3, md: 4 },
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: { xs: 1.5, sm: 0 },
            bgcolor: "background.paper",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Button
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={() => onRecordPayment(tenancy)}
              disableElevation
              fullWidth={false}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: { xs: 2, sm: 3 },
                width: { xs: "100%", sm: "auto" },
                whiteSpace: "nowrap",
              }}
            >
              Record Payment
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<HistoryIcon />}
              onClick={() => onViewHistory(tenancy)}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                borderColor: "divider",
                px: { xs: 2, sm: 3 },
                width: { xs: "100%", sm: "auto" },
                whiteSpace: "nowrap",
              }}
            >
              Payment History
            </Button>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              justifyContent: { xs: "flex-end", sm: "flex-start" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Tooltip title="View details">
              <IconButton
                onClick={() => onViewDetails(tenancy)}
                sx={{
                  color: "text.secondary",
                  "&:hover": { bgcolor: "action.hover", color: "primary.main" },
                }}
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit tenancy">
              <IconButton
                onClick={() => onEdit(tenancy)}
                sx={{
                  color: "text.secondary",
                  "&:hover": { bgcolor: "action.hover", color: "primary.main" },
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete tenancy">
              <IconButton
                onClick={() => onDelete(tenancy)}
                sx={{
                  color: "text.secondary",
                  "&:hover": { bgcolor: "error.lighter", color: "error.main" },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </CardActions>
      </Card>
    );
  },
);

// --- Main Page Component ---
export function TenanciesPage() {
  const [tenancies, setTenancies] = useState<TenancyRecord[]>([]);
  const [tenants, setTenants] = useState<TenantRecord[]>([]);
  const [rooms, setRooms] = useState<RoomRecord[]>([]);
  const [properties, setProperties] = useState<PropertyRecord[]>([]);
  const [payments, setPayments] = useState<RentPaymentRecord[]>([]);

  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<TenancyModalMode>("create");
  const [selectedTenancy, setSelectedTenancy] = useState<TenancyRecord | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tenancyToDelete, setTenancyToDelete] = useState<TenancyRecord | null>(
    null,
  );
  const [formState, setFormState] = useState<TenancyFormState>(
    createInitialFormState,
  );

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentModalMode, setPaymentModalMode] = useState<"create" | "edit">(
    "create",
  );
  const [paymentTenancy, setPaymentTenancy] = useState<TenancyRecord | null>(
    null,
  );
  const [editingPayment, setEditingPayment] =
    useState<RentPaymentRecord | null>(null);
  const [paymentToDelete, setPaymentToDelete] =
    useState<RentPaymentRecord | null>(null);
  const [paymentDeleteDialogOpen, setPaymentDeleteDialogOpen] = useState(false);
  const [paymentFormState, setPaymentFormState] = useState<PaymentFormState>(
    createInitialPaymentFormState,
  );

  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyTenancy, setHistoryTenancy] = useState<TenancyRecord | null>(
    null,
  );

  const [paymentStatusFilter, setPaymentStatusFilter] =
    useState<PaymentStatusFilterOption>("all");
  const [paymentSourceFilter] = useState<PaymentSourceFilterOption>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilterOption>("all");

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsTenancy, setDetailsTenancy] = useState<TenancyRecord | null>(
    null,
  );

  const { showAlert, showSnackbar } = useNotifications();

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [tenancyData, tenantData, roomData, propertyData, paymentData] =
        await Promise.all([
          getTenancies(),
          getTenants(),
          getRooms(),
          getProperties(),
          getRentPayments(),
        ]);
      setTenancies(tenancyData);
      setTenants(tenantData);
      setRooms(roomData);
      setProperties(propertyData);
      setPayments(paymentData);
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Unable to load tenancy data",
        message: "Please refresh and try again.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const tenantMap = useMemo(
    () =>
      tenants.reduce(
        (acc, t) => ({ ...acc, [t.id]: t }),
        {} as Record<string | number, TenantRecord>,
      ),
    [tenants],
  );

  const roomMap = useMemo(
    () =>
      rooms.reduce(
        (acc, r) => ({ ...acc, [r.id]: r }),
        {} as Record<string | number, RoomRecord>,
      ),
    [rooms],
  );

  const propertyMap = useMemo(
    () =>
      properties.reduce(
        (acc, p) => ({ ...acc, [p.id]: p }),
        {} as Record<string | number, PropertyRecord>,
      ),
    [properties],
  );

  const currentMonthKey = getCurrentMonthValue();

  const getMonthKeyForOffset = (offset: number) => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() + offset);
    return getMonthKeyFromDate(date);
  };

  const getTenancyPaymentHistory = (tenancyId: number | string) =>
    payments.filter(
      (payment) => String(payment.tenancy_id) === String(tenancyId),
    );

  const getTenancyPaymentMonthKeys = (tenancyId: number | string) =>
    getTenancyPaymentHistory(tenancyId)
      .map((payment) => {
        if (payment.payment_period) return getMonthKey(payment.payment_period);
        return getMonthKey(payment.payment_date);
      })
      .filter(Boolean);
  const monthLabel = (monthKey: string) => {
    const [year, month] = monthKey.split("-");
    if (!year || !month) return "Unknown";
    return new Intl.DateTimeFormat("en-GB", {
      month: "short",
      year: "numeric",
    }).format(new Date(`${year}-${month}-01`));
  };

  const getRecentMonthStatuses = (tenancyId: number | string) => {
    const paidKeys = new Set(getTenancyPaymentMonthKeys(tenancyId));
    return [0, -1, -2].map((offset) => {
      const key = getMonthKeyForOffset(offset);
      return { key, label: monthLabel(key), paid: paidKeys.has(key) };
    });
  };

  const hasCurrentMonthPayment = (tenancyId: number | string) =>
    getTenancyPaymentMonthKeys(tenancyId).includes(currentMonthKey);

  const filteredTenancies = useMemo(() => {
    const term = deferredSearchTerm.trim().toLowerCase();

    return tenancies.filter((tenancy) => {
      if (statusFilter !== "all" && tenancy.status !== statusFilter)
        return false;

      const paidThisMonth = hasCurrentMonthPayment(tenancy.id);
      if (paymentStatusFilter === "paid" && !paidThisMonth) return false;
      if (paymentStatusFilter === "unpaid" && paidThisMonth) return false;

      const tenancyPayments = getTenancyPaymentHistory(tenancy.id);
      if (
        paymentSourceFilter !== "all" &&
        !tenancyPayments.some((p) => p.payment_source === paymentSourceFilter)
      ) {
        return false;
      }

      if (!term) return true;

      const tenant = tenantMap[tenancy.tenant_id];
      const room = roomMap[tenancy.room_id];
      const property = room ? propertyMap[room.property_id] : undefined;

      const tenantName = tenant
        ? `${tenant.first_name} ${tenant.last_name}`.trim()
        : "";

      const haystack = [
        tenantName,
        room?.room_number ?? "",
        property?.property_name ?? "",
        tenancy.status,
        tenancy.notes ?? "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [
    deferredSearchTerm,
    tenancies,
    paymentStatusFilter,
    paymentSourceFilter,
    statusFilter,
    tenantMap,
    roomMap,
    propertyMap,
    payments,
  ]);

  // --- Handlers ---
  const openCreateModal = () => {
    setModalMode("create");
    setSelectedTenancy(null);
    setFormState({
      ...createInitialFormState(),
      tenant_id: tenants[0]?.id?.toString() ?? "",
      room_id: rooms[0]?.id?.toString() ?? "",
    });
    setIsModalOpen(true);
  };

  const openEditModal = (tenancy: TenancyRecord) => {
    setModalMode("edit");
    setSelectedTenancy(tenancy);
    setFormState({
      tenant_id: String(tenancy.tenant_id),
      room_id: String(tenancy.room_id),
      tenancy_start: normalizeDateInputValue(tenancy.tenancy_start),
      payment_date: normalizeDateInputValue(tenancy.payment_date),
      tenancy_end: normalizeDateInputValue(tenancy.tenancy_end),
      monthly_rent: tenancy.monthly_rent?.toString() ?? "",
      status: tenancy.status,
      notes: tenancy.notes ?? "",
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTenancy(null);
    setFormState(createInitialFormState());
  };

  const openPaymentModal = (tenancy: TenancyRecord) => {
    setPaymentModalMode("create");
    setEditingPayment(null);
    setPaymentTenancy(tenancy);
    setPaymentFormState({
      ...createInitialPaymentFormState(),
      payment_date: new Date().toISOString().slice(0, 10),
      payment_period: getCurrentMonthValue(),
      amount: tenancy.monthly_rent?.toString() ?? "",
    });
    setPaymentModalOpen(true);
  };

  const openEditPaymentModal = (
    payment: RentPaymentRecord,
    tenancy: TenancyRecord,
  ) => {
    setPaymentModalMode("edit");
    setEditingPayment(payment);
    setPaymentTenancy(tenancy);
    setPaymentFormState({
      payment_date: normalizeDateInputValue(payment.payment_date),
      payment_period: payment.payment_period
        ? payment.payment_period.slice(0, 7)
        : getCurrentMonthValue(),
      amount: payment.amount.toString(),
      payment_source: payment.payment_source,
      payment_method: payment.payment_method ?? "Bank transfer",
      reference_no: payment.reference_no ?? "",
      notes: payment.notes ?? "",
    });
    setPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setPaymentModalMode("create");
    setEditingPayment(null);
    setPaymentTenancy(null);
    setPaymentFormState(createInitialPaymentFormState());
  };

  const openHistoryModal = (tenancy: TenancyRecord) => {
    setHistoryTenancy(tenancy);
    setHistoryModalOpen(true);
  };

  const closeHistoryModal = () => {
    setHistoryModalOpen(false);
    setHistoryTenancy(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !formState.tenant_id ||
      !formState.room_id ||
      !formState.tenancy_start ||
      !formState.payment_date
    ) {
      showAlert({
        title: "Missing details",
        message: "Tenant, room, start date, and pay date are required.",
        variant: "error",
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        tenant_id: Number(formState.tenant_id),
        room_id: Number(formState.room_id),
        tenancy_start: formState.tenancy_start,
        payment_date: formState.payment_date,
        tenancy_end: formState.tenancy_end || null,
        monthly_rent: formState.monthly_rent
          ? Number(formState.monthly_rent)
          : null,
        status: formState.status,
        notes: formState.notes.trim() || null,
      };

      if (modalMode === "edit" && selectedTenancy) {
        await updateTenancy(selectedTenancy.id, payload);
        showSnackbar({
          title: "Tenancy updated",
          message: "The tenancy details were updated successfully.",
          variant: "success",
        });
      } else {
        await createTenancy(payload);
        showSnackbar({
          title: "Tenancy created",
          message: "The tenancy is now linked to the tenant and room.",
          variant: "success",
        });
      }
      closeModal();
      await loadData();
    } catch (error) {
      console.error(error);
      showAlert({
        title: modalMode === "edit" ? "Unable to update" : "Unable to create",
        message: "Please review the details and try again.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!paymentTenancy) return;

    if (!paymentFormState.amount || Number(paymentFormState.amount) <= 0) {
      showAlert({
        title: "Invalid amount",
        message: "Please enter a payment amount greater than zero.",
        variant: "error",
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        tenancy_id: paymentTenancy.id,
        payment_date: paymentFormState.payment_date,
        payment_period:
          normalizePeriodValue(paymentFormState.payment_period) || null,
        amount: Number(paymentFormState.amount),
        payment_source: paymentFormState.payment_source,
        payment_method: paymentFormState.payment_method.trim() || null,
        reference_no: paymentFormState.reference_no.trim() || null,
        notes: paymentFormState.notes.trim() || null,
      };

      if (paymentModalMode === "edit" && editingPayment) {
        await updateRentPayment(editingPayment.id, payload);
        showSnackbar({
          title: "Payment updated",
          message: "The rent payment was updated successfully.",
          variant: "success",
        });
      } else {
        await createRentPayment(payload);
        showSnackbar({
          title: "Payment recorded",
          message: "The rent payment was saved successfully.",
          variant: "success",
        });
      }

      closePaymentModal();
      await loadData();
    } catch (error) {
      console.error(error);
      showAlert({
        title:
          paymentModalMode === "edit"
            ? "Unable to update payment"
            : "Unable to record payment",
        message: "Please try again or check the payment details.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!tenancyToDelete) return;
    setDeleteDialogOpen(false);
    setIsLoading(true);
    try {
      await deleteTenancy(tenancyToDelete.id);
      await loadData();
      showSnackbar({
        title: "Tenancy deleted",
        message: "The tenancy record was removed.",
        variant: "info",
      });
    } catch (error) {
      console.error(error);
      const message =
        error instanceof Error
          ? error.message
          : "This tenancy could not be removed.";
      showAlert({
        title: "Unable to delete tenancy",
        message,
        variant: "error",
      });
    } finally {
      setIsLoading(false);
      setTenancyToDelete(null);
    }
  };

  return (
    <Stack spacing={4} sx={{ width: "100%", maxWidth: "100%" }}>
      {/* Top Filter and Actions Bar - Unified Control Panel */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 4,
          p: { xs: 2, md: 3 },
          bgcolor: "background.paper",
          boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
        >
          {/* Header Title & Actions */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            {/* Mobile Actions Only (Desktop floats right) */}
            <Stack
              direction="row"
              spacing={1}
              sx={{ display: { xs: "flex", md: "none" } }}
            >
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={() => void loadData()}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 3,
                  }}
                >
                  <RefreshRounded fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                onClick={openCreateModal}
                disableElevation
                sx={{ borderRadius: 3, minWidth: 40, p: 1 }}
              >
                <AddIcon />
              </Button>
            </Stack>
          </Stack>

          {/* Search & Filters */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{
              flexGrow: 1,
              alignItems: "center",
            }}
          >
            <TextField
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search tenants, properties..."
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxHeight: 40,
                "& .MuiOutlinedInput-root": { borderRadius: 6 },
              }}
            />
            <FormControl
              size="small"
              sx={{
                minWidth: 140,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as StatusFilterOption)
                }
                sx={{ borderRadius: 6 }}
                displayEmpty
              >
                <MenuItem value="all">All statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="notice_given">Notice given</MenuItem>
                <MenuItem value="ended">Ended</MenuItem>
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 150, width: { xs: "100%", sm: "auto" } }}
            >
              <Select
                value={paymentStatusFilter}
                onChange={(e) =>
                  setPaymentStatusFilter(
                    e.target.value as PaymentStatusFilterOption,
                  )
                }
                sx={{ borderRadius: 6 }}
                displayEmpty
              >
                <MenuItem value="all">All payments</MenuItem>
                <MenuItem value="paid">Paid this month</MenuItem>
                <MenuItem value="unpaid">Unpaid this month</MenuItem>
              </Select>
            </FormControl>

            {/* Desktop Actions */}
            <Stack
              direction="row"
              spacing={1}
              sx={{ display: { xs: "none", md: "flex" }, pl: 1 }}
            >
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={() => void loadData()}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 6,
                  }}
                >
                  <RefreshRounded fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreateModal}
                disableElevation
                sx={{
                  borderRadius: 6,
                  px: 3,
                  whiteSpace: "nowrap",
                  fontWeight: 600,
                }}
              >
                Add
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {/* Main Full-Width Stack Area */}
      <Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontWeight: 600,
            mb: 2,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          {isLoading
            ? "Loading..."
            : `Showing ${filteredTenancies.length} records`}
        </Typography>

        {isLoading && tenancies.length === 0 ? (
          <Stack spacing={3} sx={{ width: "100%" }}>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={220}
                sx={{ borderRadius: 4, width: "100%" }}
              />
            ))}
          </Stack>
        ) : (
          <Stack spacing={3} sx={{ width: "100%" }}>
            {filteredTenancies.map((tenancy) => (
              <TenancyCard
                key={tenancy.id}
                tenancy={tenancy}
                tenant={tenantMap[tenancy.tenant_id]}
                room={roomMap[tenancy.room_id]}
                hasPaidCurrentMonth={hasCurrentMonthPayment(tenancy.id)}
                recentStatuses={getRecentMonthStatuses(tenancy.id)}
                onEdit={openEditModal}
                onDelete={() => {
                  setTenancyToDelete(tenancy);
                  setDeleteDialogOpen(true);
                }}
                onRecordPayment={openPaymentModal}
                onViewHistory={openHistoryModal}
                onViewDetails={(t) => {
                  setDetailsTenancy(t);
                  setDetailsModalOpen(true);
                }}
              />
            ))}
          </Stack>
        )}

        {!isLoading && filteredTenancies.length === 0 && (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 4,
              py: 10,
              textAlign: "center",
              bgcolor: "transparent",
              borderStyle: "dashed",
            }}
          >
            <SearchIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
            <Typography
              variant="h6"
              color="text.primary"
              sx={{ fontWeight: 700 }}
            >
              No tenancies found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try adjusting your search criteria or filters.
            </Typography>
          </Paper>
        )}
      </Box>

      {/* ---------------- MODALS ---------------- */}

      {/* 1. Create/Edit Tenancy Modal */}
      <Dialog
        open={isModalOpen}
        onClose={closeModal}
        PaperProps={{ sx: { borderRadius: 6, width: "100%", maxWidth: 720 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {modalMode === "edit" ? "Edit tenancy" : "Add tenancy"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Capture the tenancy details, occupancy, and terms in one place.
          </DialogContentText>
          <Box
            component="form"
            id="tenancy-form"
            onSubmit={handleSubmit}
            noValidate
          >
            <Stack spacing={2.25} sx={{ mt: 1 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <FormControl fullWidth>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, fontWeight: 600 }}
                  >
                    Tenant
                  </Typography>
                  <Select
                    value={formState.tenant_id}
                    onChange={(e) =>
                      setFormState((c) => ({ ...c, tenant_id: e.target.value }))
                    }
                    sx={{ borderRadius: 6 }}
                  >
                    {tenants.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenant.first_name} {tenant.last_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, fontWeight: 600 }}
                  >
                    Room
                  </Typography>
                  <Select
                    value={formState.room_id}
                    onChange={(e) =>
                      setFormState((c) => ({ ...c, room_id: e.target.value }))
                    }
                    sx={{ borderRadius: 6 }}
                  >
                    {rooms.map((room) => {
                      const propName =
                        propertyMap[room.property_id]?.property_name ??
                        "Unknown";
                      return (
                        <MenuItem key={room.id} value={room.id}>
                          {propName} · Room {room.room_number}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  value={formState.tenancy_start}
                  onChange={(e) =>
                    setFormState((c) => ({
                      ...c,
                      tenancy_start: e.target.value,
                    }))
                  }
                  label="Start date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
                />
                <TextField
                  value={formState.tenancy_end}
                  onChange={(e) =>
                    setFormState((c) => ({ ...c, tenancy_end: e.target.value }))
                  }
                  label="End date (Optional)"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
                />
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  value={formState.monthly_rent}
                  onChange={(e) =>
                    setFormState((c) => ({
                      ...c,
                      monthly_rent: e.target.value,
                    }))
                  }
                  label="Monthly rent (£)"
                  type="number"
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
                />
                <TextField
                  value={formState.payment_date}
                  onChange={(e) =>
                    setFormState((c) => ({
                      ...c,
                      payment_date: e.target.value,
                    }))
                  }
                  label="Pay date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
                />
              </Stack>
              <FormControl fullWidth>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5, fontWeight: 600 }}
                >
                  Status
                </Typography>
                <Select
                  value={formState.status}
                  onChange={(e) =>
                    setFormState((c) => ({ ...c, status: e.target.value }))
                  }
                  sx={{ borderRadius: 6 }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="ended">Ended</MenuItem>
                  <MenuItem value="notice_given">Notice given</MenuItem>
                </Select>
              </FormControl>
              <TextField
                value={formState.notes}
                onChange={(e) =>
                  setFormState((c) => ({ ...c, notes: e.target.value }))
                }
                label="Notes & Terms"
                multiline
                minRows={3}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeModal}
            color="inherit"
            sx={{ borderRadius: 6, fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="tenancy-form"
            variant="contained"
            disableElevation
            sx={{ borderRadius: 6, fontWeight: 600 }}
          >
            {modalMode === "edit" ? "Save changes" : "Create tenancy"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2. Record Payment Modal */}
      <Dialog
        open={paymentModalOpen}
        onClose={closePaymentModal}
        PaperProps={{ sx: { borderRadius: 4, width: "100%", maxWidth: 500 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {paymentModalMode === "edit"
            ? "Update rent payment"
            : "Record rent payment"}
        </DialogTitle>
        <DialogContent dividers>
          <Box
            component="form"
            id="payment-form"
            onSubmit={handlePaymentSubmit}
            noValidate
          >
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2}>
                <TextField
                  value={paymentFormState.payment_date}
                  onChange={(e) =>
                    setPaymentFormState((c) => ({
                      ...c,
                      payment_date: e.target.value,
                    }))
                  }
                  label="Payment date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
                <TextField
                  value={paymentFormState.payment_period}
                  onChange={(e) =>
                    setPaymentFormState((c) => ({
                      ...c,
                      payment_period: e.target.value,
                    }))
                  }
                  label="For period"
                  type="month"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Stack>
              <TextField
                value={paymentFormState.amount}
                onChange={(e) =>
                  setPaymentFormState((c) => ({ ...c, amount: e.target.value }))
                }
                label="Amount (£)"
                type="number"
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <Stack direction="row" spacing={2}>
                <FormControl fullWidth>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, fontWeight: 600 }}
                  >
                    Source
                  </Typography>
                  <Select
                    value={paymentFormState.payment_source}
                    onChange={(e) =>
                      setPaymentFormState((c) => ({
                        ...c,
                        payment_source: e.target.value as any,
                      }))
                    }
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="tenant">Tenant</MenuItem>
                    <MenuItem value="government">Government</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, fontWeight: 600 }}
                  >
                    Method
                  </Typography>
                  <Select
                    value={paymentFormState.payment_method}
                    onChange={(e) =>
                      setPaymentFormState((c) => ({
                        ...c,
                        payment_method: e.target.value,
                      }))
                    }
                    sx={{ borderRadius: 2 }}
                  >
                    {PAYMENT_METHOD_OPTIONS.map((method) => (
                      <MenuItem key={method} value={method}>
                        {method}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <TextField
                value={paymentFormState.reference_no}
                onChange={(e) =>
                  setPaymentFormState((c) => ({
                    ...c,
                    reference_no: e.target.value,
                  }))
                }
                label="Reference number"
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <TextField
                value={paymentFormState.notes}
                onChange={(e) =>
                  setPaymentFormState((c) => ({ ...c, notes: e.target.value }))
                }
                label="Notes"
                multiline
                minRows={2}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 3, bgcolor: "background.default" }}>
          <Button
            onClick={closePaymentModal}
            color="inherit"
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="payment-form"
            variant="contained"
            disableElevation
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {paymentModalMode === "edit" ? "Save payment" : "Confirm payment"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 3. Payment History Modal */}
      <Dialog
        open={historyModalOpen}
        onClose={closeHistoryModal}
        PaperProps={{ sx: { borderRadius: 4, width: "100%", maxWidth: 800 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1.5 }}>
          Payment History
          {historyTenancy && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75, fontWeight: 500 }}
            >
              {tenantMap[historyTenancy.tenant_id]?.first_name}{" "}
              {tenantMap[historyTenancy.tenant_id]?.last_name}
              {" • "}
              {
                propertyMap[roomMap[historyTenancy.room_id]?.property_id]
                  ?.property_name
              }{" "}
              (Room {roomMap[historyTenancy.room_id]?.room_number})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {historyTenancy &&
          getTenancyPaymentHistory(historyTenancy.id).length > 0 ? (
            <TableContainer>
              <Table stickyHeader size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "background.default" }}
                    >
                      Date
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "background.default" }}
                    >
                      Period
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "background.default" }}
                    >
                      Source
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "background.default" }}
                    >
                      Method
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, bgcolor: "background.default" }}
                    >
                      Amount
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, bgcolor: "background.default" }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getTenancyPaymentHistory(historyTenancy.id)
                    .sort(
                      (a, b) =>
                        new Date(b.payment_date).getTime() -
                        new Date(a.payment_date).getTime(),
                    )
                    .map((payment) => (
                      <TableRow key={payment.id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {formatDate(payment.payment_date)}
                        </TableCell>
                        <TableCell sx={{ color: "text.secondary" }}>
                          {payment.payment_period
                            ? monthLabel(getMonthKey(payment.payment_period))
                            : "—"}
                        </TableCell>
                        <TableCell
                          sx={{
                            textTransform: "capitalize",
                            color: "text.secondary",
                          }}
                        >
                          {payment.payment_source}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {payment.payment_method ?? "—"}
                          </Typography>
                          {payment.reference_no && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Ref: {payment.reference_no}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, color: "text.primary" }}
                        >
                          {formatCurrency(Number(payment.amount))}
                        </TableCell>
                        <TableCell align="right" sx={{ width: 110 }}>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                          >
                            <Tooltip title="Edit payment">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  openEditPaymentModal(payment, historyTenancy)
                                }
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete payment">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setPaymentToDelete(payment);
                                  setPaymentDeleteDialogOpen(true);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <HistoryIcon
                sx={{ fontSize: 48, color: "text.disabled", mb: 2 }}
              />
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                No payments found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This tenancy doesn't have any recorded rent payments yet.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "background.default" }}>
          <Button
            onClick={closeHistoryModal}
            variant="contained"
            color="inherit"
            disableElevation
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={paymentDeleteDialogOpen}
        title="Delete payment"
        message="Delete this payment? This cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
        onClose={() => {
          setPaymentDeleteDialogOpen(false);
          setPaymentToDelete(null);
        }}
        onConfirm={async () => {
          if (!paymentToDelete) return;
          setPaymentDeleteDialogOpen(false);
          setIsLoading(true);
          try {
            await deleteRentPayment(paymentToDelete.id);
            showSnackbar({
              title: "Payment deleted",
              message: "The payment was removed successfully.",
              variant: "success",
            });
            await loadData();
          } catch (error) {
            console.error(error);
            showAlert({
              title: "Unable to delete payment",
              message: "Please try again or refresh the page.",
              variant: "error",
            });
          } finally {
            setIsLoading(false);
            setPaymentToDelete(null);
          }
        }}
      />

      {/* 4. Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete tenancy</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.primary" }}>
            Are you sure you want to delete this tenancy? This action cannot be
            undone and will orphan associated payment records.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 3, bgcolor: "background.default" }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="inherit"
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disableElevation
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* 5. Tenancy Details Modal */}
      <Dialog
        open={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setDetailsTenancy(null);
        }}
        PaperProps={{ sx: { borderRadius: 6, width: "100%", maxWidth: 720 } }}
      >
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                bgcolor: stringToColor(
                  detailsTenancy
                    ? `${tenantMap[detailsTenancy.tenant_id]?.first_name ?? ""} ${tenantMap[detailsTenancy.tenant_id]?.last_name ?? ""}`
                    : "",
                ),
                width: 42,
                height: 42,
                fontWeight: 700,
              }}
            >
              {(
                tenantMap[detailsTenancy?.tenant_id ?? ""]?.first_name ?? "T"
              ).charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {tenantMap[detailsTenancy?.tenant_id ?? ""]?.first_name ?? ""}{" "}
                {tenantMap[detailsTenancy?.tenant_id ?? ""]?.last_name ?? ""}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Room{" "}
                {roomMap[detailsTenancy?.room_id ?? ""]?.room_number ?? "—"}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {/* Status */}
            <Box>
              <Chip
                label={
                  detailsTenancy?.status === "active"
                    ? "Active"
                    : detailsTenancy?.status === "notice_given"
                      ? "Notice Given"
                      : "Ended"
                }
                color={
                  detailsTenancy?.status === "active"
                    ? "success"
                    : detailsTenancy?.status === "notice_given"
                      ? "warning"
                      : "default"
                }
                sx={{ borderRadius: 6, fontWeight: 600 }}
              />
            </Box>

            {/* Property & Room */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "primary.main",
                }}
              >
                Property & Room
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Property
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {(() => {
                      const room = roomMap[detailsTenancy?.room_id ?? ""];
                      if (!room) return "—";
                      const prop = properties.find(
                        (p) => p.id === room.property_id,
                      );
                      return prop?.property_name ?? "—";
                    })()}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Address
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {(() => {
                      const room = roomMap[detailsTenancy?.room_id ?? ""];
                      if (!room) return "—";
                      const prop = properties.find(
                        (p) => p.id === room.property_id,
                      );
                      return prop
                        ? `${prop.address_line_1}, ${prop.city}, ${prop.postcode}`
                        : "—";
                    })()}
                  </Typography>
                </Box>
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 1.5 }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Room Number
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {roomMap[detailsTenancy?.room_id ?? ""]?.room_number ?? "—"}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Floor
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {roomMap[detailsTenancy?.room_id ?? ""]?.floor_number !=
                    null
                      ? roomMap[detailsTenancy?.room_id ?? ""]?.floor_number
                      : "—"}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Tenancy Terms */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "primary.main",
                }}
              >
                Tenancy Terms
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Start Date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDate(detailsTenancy?.tenancy_start) ?? "—"}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    End Date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {detailsTenancy?.tenancy_end
                      ? formatDate(detailsTenancy.tenancy_end)
                      : "Open ended"}
                  </Typography>
                </Box>
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 1.5 }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Monthly Rent
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    £{Number(detailsTenancy?.monthly_rent ?? 0).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Pay Date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDate(detailsTenancy?.payment_date) ?? "—"}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Payment Summary */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "primary.main",
                }}
              >
                Payment Summary
              </Typography>
              {(() => {
                const tenancyPayments = payments.filter(
                  (p) => p.tenancy_id === detailsTenancy?.id,
                );
                const totalPaid = tenancyPayments.reduce(
                  (sum, p) => sum + Number(p.amount),
                  0,
                );
                return (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Total Payments
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tenancyPayments.length} payment
                        {tenancyPayments.length !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Total Amount Paid
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        £{totalPaid.toFixed(2)}
                      </Typography>
                    </Box>
                  </Stack>
                );
              })()}
            </Box>

            {/* Notes */}
            {detailsTenancy?.notes && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "primary.main",
                  }}
                >
                  Notes
                </Typography>
                <Typography variant="body2">{detailsTenancy.notes}</Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setDetailsModalOpen(false);
              setDetailsTenancy(null);
            }}
            color="inherit"
            sx={{ borderRadius: 6 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
